import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";

//const API_URL = "http://localhost:8080/api/standardlist";
const API_URL = "/api/standardlist";

function StandardList() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error("Failed to fetch data");
      const jsonData = await response.json();
      setData(jsonData);
      setFilteredData(jsonData);
      setSelectedRows([]);
      setRowErrors({});
    } catch (err) {
      console.error(err);
      alert("Failed to fetch data from backend.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!keyword) setFilteredData(data);
  }, [data, keyword]);

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const isValidDate = (dateStr) => {
    if (!dateStr) return true;
    const d = new Date(dateStr);
    return !isNaN(d.getTime());
  };

  const handleCellChange = (id, field, value) => {
    setData((prevData) =>
      prevData.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );

    if (field === "calDate" || field === "dueDate") {
      setRowErrors((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: !isValidDate(value),
        },
      }));
    }
  };

  const sanitizeRow = (row) => ({
    ...row,
    calDate: row.calDate || null,
    dueDate: row.dueDate || null,
  });

  const saveRow = async (row) => {
    if (!isValidDate(row.calDate) || !isValidDate(row.dueDate)) {
      alert("CalDate and DueDate must be valid dates (YYYY-MM-DD or similar).");
      return;
    }

    try {
      const isNew = row.id?.toString().startsWith("temp-");
      const url = isNew ? API_URL : `${API_URL}/${row.id}`;
      const method = isNew ? "POST" : "PUT";

      const sanitizedRow = sanitizeRow(row);
      if (isNew) delete sanitizedRow.id;

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sanitizedRow),
      });

      if (!response.ok) throw new Error("Failed to save row");
      setEditRowId(null);
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to save row.");
    }
  };

  const addRow = () => {
    const newRow = {
      id: `temp-${Date.now()}`,
      standardId: "",
      standardType: "",
      manufacturer: "",
      model: "",
      calDate: "",
      dueDate: "",
    };
    setData([newRow, ...data]);
    setFilteredData([newRow, ...filteredData]);
    setEditRowId(newRow.id);
  };

  // ✅ Delete confirmation requires typing 'yes' or 'y'
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) {
      alert("No rows selected to delete.");
      return;
    }

    const input = window.prompt(
      "To confirm deleting selected rows, type 'yes' or 'y'"
    );

    if (!input || !["yes", "y"].includes(input.toLowerCase())) {
      alert("Delete cancelled.");
      return;
    }

    try {
      for (const id of selectedRows) {
        if (id && !id.toString().startsWith("temp-")) {
          await fetch(`${API_URL}/${id}`, { method: "DELETE" });
        }
      }
      alert("Deleted successfully");
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to delete row(s).");
    }
  };

  const copySelectedRows = () => {
    if (selectedRows.length === 0) {
      alert("No rows selected to copy.");
      return;
    }

    const overdueRows = data.filter(
      (row) =>
        selectedRows.includes(row.id) &&
        row.dueDate &&
        new Date(row.dueDate) < new Date()
    );

    if (overdueRows.length > 0) {
      alert("Cannot copy rows with past due dates.");
      return;
    }

    // Exclude the 'id' field when copying
    const copiedText = data
      .filter((row) => selectedRows.includes(row.id))
      .map((row) =>
        Object.entries(row)
          .filter(([key]) => key.toLowerCase() !== "id") // Exclude 'id'
          .map(([_, value]) => value)
          .join("\t")
      )
      .join("\n");

    if (!copiedText) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(copiedText).catch(console.error);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = copiedText;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
      document.body.removeChild(textarea);
    }
  };

  const filterRows = () => {
    if (!keyword) {
      setFilteredData(data);
      return;
    }
    const lowerKeyword = keyword.toLowerCase();
    setFilteredData(
      data.filter((row) =>
        Object.values(row).some((val) =>
          val?.toString().toLowerCase().includes(lowerKeyword)
        )
      )
    );
  };

  const showSelected = () =>
    setFilteredData(data.filter((row) => selectedRows.includes(row.id)));
  const showAll = () => setFilteredData(data);

  return (
    <Box p={2}>
      {/* Controls */}
      <Box mb={2} display="flex" gap={2}>
        <Button variant="contained" color="primary" onClick={copySelectedRows}>
          Copy Selected
        </Button>
        <TextField
          label="Keyword"
          variant="outlined"
          size="small"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button variant="contained" onClick={filterRows}>
          Search
        </Button>
        <Button variant="contained" onClick={showAll}>
          Show All
        </Button>
        <Button variant="contained" onClick={showSelected}>
          Show Selected
        </Button>
        <Button variant="contained" color="primary" onClick={addRow}>
          Add Row
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={deleteSelectedRows}
        >
          Delete Selected
        </Button>
      </Box>

      {/* Table */}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {data[0] &&
                  Object.keys(data[0])
                    .filter(
                      (key) =>
                        key.toLowerCase() !== "id" && !/^column\d+$/i.test(key)
                    )
                    .map((key) => <TableCell key={key}>{key}</TableCell>)}
                <TableCell>Edit</TableCell>
                <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row) => (
                <TableRow
                  key={row.id}
                  selected={selectedRows.includes(row.id)}
                  onClick={() => toggleRowSelection(row.id)}
                  style={{ cursor: "pointer" }}
                >
                  {Object.entries(row)
                    .filter(
                      ([key]) =>
                        key.toLowerCase() !== "id" && !/^column\d+$/i.test(key)
                    )
                    .map(([key, value]) => {
                      let style = {};
                      if (
                        key.toLowerCase() === "duedate" &&
                        isValidDate(value) &&
                        new Date(value) < new Date()
                      ) {
                        style = { backgroundColor: "red", color: "white" };
                      }

                      const hasError = rowErrors[row.id]?.[key];

                      return (
                        <TableCell key={key} style={style}>
                          {editRowId === row.id ||
                          row.id?.toString().startsWith("temp-") ? (
                            <input
                              style={{
                                width: "100%",
                                border: hasError ? "1px solid red" : "none",
                              }}
                              value={value || ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handleCellChange(row.id, key, e.target.value)
                              }
                            />
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ minWidth: 0, px: 1, py: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditRowId(row.id);
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      sx={{ minWidth: 0, px: 1, py: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        saveRow(row);
                      }}
                    >
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default StandardList;
